import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import DimDrawing from "@/components/dims/DimDrawing";

const EMPTY = { enabled: true, image_url: "", tolerance: 0.5, fields: [] };

export default function DimSearchEditor({ category, onClose, onSaved }) {
    const [cfg, setCfg] = useState({ ...EMPTY, ...(category.dim_search || {}) });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const setField = (key, patch) => setCfg((c) => ({ ...c, fields: c.fields.map((f) => (f.key === key ? { ...f, ...patch } : f)) }));
    const setAt = (i, patch) => setCfg((c) => ({ ...c, fields: c.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) }));
    const addField = () => {
        const used = new Set(cfg.fields.map((f) => f.key));
        const key = "ABCDEFGHIJKLMNOP".split("").find((k) => !used.has(k)) || `X${cfg.fields.length}`;
        setCfg((c) => ({ ...c, fields: [...c.fields, { key, label: "", x: 45, y: 45, rotate: 0, on_drawing: true }] }));
    };

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            setCfg((c) => ({ ...c, image_url: data.url }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ngarkimi dështoi");
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/categories/${category.id}/dim-search`, { ...cfg, tolerance: Number(cfg.tolerance) || 0 });
            toast.success("Filtri i dimensioneve u ruajt");
            onSaved(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Ruajtja dështoi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white w-full max-w-6xl my-6 border border-slate-200 rounded-xl" onClick={(e) => e.stopPropagation()} data-testid="dim-editor-modal">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                    <div className="font-display font-black uppercase">Kërkimi me dimensione · {category.name}</div>
                    <button onClick={onClose} data-testid="dim-editor-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 grid lg:grid-cols-[340px_1fr] gap-6">
                    <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="dim-editor-enabled" />
                            Aktiv në katalog
                        </label>
                        <div>
                            <div className="label-kicker mb-1">Toleranca (± mm)</div>
                            <input type="number" step="0.1" min="0" value={cfg.tolerance} onChange={(e) => setCfg({ ...cfg, tolerance: e.target.value })} className="input-field" data-testid="dim-editor-tolerance" />
                        </div>
                        <div>
                            <div className="label-kicker mb-1">Foto teknike</div>
                            <input value={cfg.image_url} onChange={(e) => setCfg({ ...cfg, image_url: e.target.value })} className="input-field mb-2" placeholder="URL" data-testid="dim-editor-image-url" />
                            <input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="text-xs" data-testid="dim-editor-image-upload" />
                            {uploading && <div className="text-xs font-mono text-slate-500 mt-1">Duke ngarkuar…</div>}
                        </div>
                        <div className="label-kicker pt-2">Fushat</div>
                        <div className="space-y-2 max-h-[340px] overflow-auto pr-1">
                            {cfg.fields.map((f, i) => (
                                <div key={i} className="grid grid-cols-[48px_1fr_auto_auto_auto] gap-1.5 items-center" data-testid={`dim-editor-field-${f.key}`}>
                                    <input value={f.key} onChange={(e) => setAt(i, { key: e.target.value })} className="input-field !px-2 !py-1 font-black" />
                                    <input value={f.label} onChange={(e) => setAt(i, { label: e.target.value })} className="input-field !px-2 !py-1 text-xs" placeholder="Përshkrimi" />
                                    <button type="button" title="Rrotullo" onClick={() => setAt(i, { rotate: f.rotate === 270 ? 0 : 270 })} className="text-[10px] font-mono px-1.5 py-1 border border-slate-300 rounded">{f.rotate || 0}°</button>
                                    <input type="checkbox" title="Shfaq në foto" checked={f.on_drawing !== false} onChange={(e) => setAt(i, { on_drawing: e.target.checked })} />
                                    <button type="button" onClick={() => setCfg((c) => ({ ...c, fields: c.fields.filter((_, j) => j !== i) }))} className="text-red-600"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addField} className="btn-outline w-full" data-testid="dim-editor-add-field"><Plus className="w-4 h-4" /> Shto fushë</button>
                    </div>
                    <div>
                        <p className="text-xs text-slate-500 mb-2">Tërhiqni kutitë e shkronjave mbi foto për t'i pozicionuar.</p>
                        <div className="admin-light bg-white rounded-lg border border-slate-200 px-8 py-6">
                            <DimDrawing config={cfg} editable onMove={(k, x, y) => setField(k, { x, y })} />
                        </div>
                        <div className="flex justify-end mt-4">
                            <button onClick={save} disabled={saving} className="btn-primary" data-testid="dim-editor-save"><Save className="w-4 h-4" /> {saving ? "Duke ruajtur…" : "Ruaj"}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
